import { Asset } from '../../../models/image/dto/asset';
import { MediaManagerAssetLibraryDto } from '../../../models/image/dto/media-manager-asset-library-dto';
import { MediaManagerFolderDto } from '../../../models/image/dto/media-manager-folder-dto';
import { exists } from '../../../functions/exists';
import { findFolderInLibrary } from '../mutations/asset-library-lookups';
import {
  mediaManagerSpecialFolderDisplayNames,
  MediaManagerSpecialFolderId
} from 'src/app/models/media-manager/media-manager-special-folder';
import { MediaManagerFileSystem } from 'src/app/models/media-manager/media-manager-file-system';

/**
 * Rules for a top-level special folder (POS Media, POS Assets, etc.). `lockSelf` prevents the folder itself
 * from being renamed/moved/deleted. `descendantsPreventStructuralChanges` prevents structural mutations inside
 * the folder subtree.
 */
export interface MediaManagerSpecialFolderPolicy {
  id: string;
  displayName: string;
  lockSelf: boolean;
  descendantsPreventStructuralChanges: boolean;
}

/**
 * Registry of every special-folder policy keyed by its stable id. Add new top-level special folders here.
 */
export const specialFolderPoliciesById: ReadonlyMap<string, MediaManagerSpecialFolderPolicy> = new Map<
  string,
  MediaManagerSpecialFolderPolicy
>([
  [
    MediaManagerSpecialFolderId.PosMedia,
    {
      id: MediaManagerSpecialFolderId.PosMedia,
      displayName: mediaManagerSpecialFolderDisplayNames[MediaManagerSpecialFolderId.PosMedia],
      lockSelf: true,
      descendantsPreventStructuralChanges: true
    }
  ],
  [
    MediaManagerSpecialFolderId.PosAssets,
    {
      id: MediaManagerSpecialFolderId.PosAssets,
      displayName: mediaManagerSpecialFolderDisplayNames[MediaManagerSpecialFolderId.PosAssets],
      lockSelf: true,
      descendantsPreventStructuralChanges: true
    }
  ]
]);

/** Same policies, indexed by the trimmed lower-cased display name so name-based lookups are case-insensitive. */
export const specialFolderPoliciesByCanonicalName: ReadonlyMap<string, MediaManagerSpecialFolderPolicy> = new Map(
  Array.from(specialFolderPoliciesById.values()).map(policy => [policy.displayName.trim().toLowerCase(), policy])
);

/** Trim + lowercase a folder name for case-insensitive comparison, returning null for empty input. */
export function normalizeFolderName(folderName: string | null | undefined): string | null {
  const normalizedFolderName = folderName?.trim().toLowerCase();
  return normalizedFolderName ? normalizedFolderName : null;
}

const reservedVirtualSectionFolderNames: ReadonlyMap<string, string> = new Map([['logos', 'Logos']]);

export function getReservedVirtualSectionName(folderName: string | null | undefined): string | null {
  const canonicalFolderName = normalizeFolderName(folderName);
  if (!canonicalFolderName) return null;
  return reservedVirtualSectionFolderNames.get(canonicalFolderName) ?? null;
}

/**
 * Look up the policy for a folder by id, restricted to policies whose folder is currently active (as
 * determined by the filesystem's active-special-folder configuration). Returns null when the id is not a
 * special folder or when the special folder is not active in the current environment.
 */
export function getSpecialFolderPolicyByFolderId(
  folderId: string | null | undefined,
  filesystem: MediaManagerFileSystem
): MediaManagerSpecialFolderPolicy | null {
  if (!folderId || !filesystem.isActiveSpecialTopLevelFolderId(folderId)) return null;
  return specialFolderPoliciesById.get(folderId) ?? null;
}

/** Same as {@link getSpecialFolderPolicyByFolderId} but resolves by display name. */
export function getSpecialFolderPolicyByFolderName(
  folderName: string | null | undefined,
  filesystem: MediaManagerFileSystem
): MediaManagerSpecialFolderPolicy | null {
  const canonicalFolderName = normalizeFolderName(folderName);
  const policy = canonicalFolderName ? specialFolderPoliciesByCanonicalName.get(canonicalFolderName) : null;
  return policy && filesystem.isActiveSpecialTopLevelFolderId(policy.id) ? policy : null;
}

/** Ids of every active special folder whose descendants are structurally protected. */
export function getProtectedSpecialFolderIds(
  assetLibrary: MediaManagerAssetLibraryDto,
  filesystem: MediaManagerFileSystem
): Set<string> {
  return new Set(
    assetLibrary.assetFolders
      .filter(folder => {
        const policy = getSpecialFolderPolicyByFolderId(folder.id, filesystem);
        return exists(policy?.descendantsPreventStructuralChanges);
      })
      .map(folder => folder.id)
  );
}

/**
 * Look up the nearest protected top-level special folder that contains this folder (or optionally the folder
 * itself). Returns null when the folder sits outside every protected subtree.
 */
export function getProtectedTopLevelSpecialFolderForFolderId(
  folderId: string | null | undefined,
  filesystem: MediaManagerFileSystem,
  assetLibrary: MediaManagerAssetLibraryDto,
  includeFolderItself: boolean = true
): MediaManagerFolderDto | null {
  if (!folderId?.trim()) {
    return null;
  }
  if (includeFolderItself) {
    const folder = findFolderInLibrary(folderId, assetLibrary);
    if (folder && getSpecialFolderPolicyByFolderId(folder.id, filesystem)?.descendantsPreventStructuralChanges) {
      return folder;
    }
  }
  const protectedFolderIds = getProtectedSpecialFolderIds(assetLibrary, filesystem);
  const ancestorFolderId = filesystem.folderHasAnyAncestorInSet(folderId, protectedFolderIds);
  return ancestorFolderId ? findFolderInLibrary(ancestorFolderId, assetLibrary) : null;
}

/** Look up the nearest protected top-level special folder that contains this asset. */
export function getProtectedTopLevelSpecialFolderForAssetId(
  assetId: string | null | undefined,
  filesystem: MediaManagerFileSystem,
  assetLibrary: MediaManagerAssetLibraryDto
): MediaManagerFolderDto | null {
  if (!assetId?.trim()) {
    return null;
  }
  const protectedFolderIds = getProtectedSpecialFolderIds(assetLibrary, filesystem);
  const ancestorFolderId = filesystem.assetHasAnyAncestorInSet(assetId, protectedFolderIds);
  return ancestorFolderId ? findFolderInLibrary(ancestorFolderId, assetLibrary) : null;
}

/** Excludes the folder itself, so the ancestor lookup only returns something when the folder is nested. */
export function getProtectedSpecialAncestorForFolder(
  folder: MediaManagerFolderDto,
  filesystem: MediaManagerFileSystem,
  assetLibrary: MediaManagerAssetLibraryDto
): MediaManagerFolderDto | null {
  return getProtectedTopLevelSpecialFolderForFolderId(folder.id, filesystem, assetLibrary, false);
}

/** Look up the nearest protected top-level special folder that contains this asset. */
export function getProtectedSpecialAncestorForAsset(
  asset: Asset,
  filesystem: MediaManagerFileSystem,
  assetLibrary: MediaManagerAssetLibraryDto
): MediaManagerFolderDto | null {
  return getProtectedTopLevelSpecialFolderForAssetId(asset.getUniqueIdentifier(), filesystem, assetLibrary);
}

/**
 * Returns the reserved-name policy that would conflict with an attempted rename/create for the given name, or
 * null when the name is either not reserved or when no other folder in the library currently owns it.
 */
export function getReservedFolderNameConflict(
  folderName: string | null | undefined,
  currentFolderId: string | null | undefined,
  assetLibrary: MediaManagerAssetLibraryDto,
  filesystem: MediaManagerFileSystem
): MediaManagerSpecialFolderPolicy | null {
  const canonicalFolderName = normalizeFolderName(folderName);
  if (!canonicalFolderName) {
    return null;
  }
  const policy = specialFolderPoliciesByCanonicalName.get(canonicalFolderName);
  if (!policy || !filesystem.isActiveSpecialTopLevelFolderId(policy.id)) {
    return null;
  }
  if (!assetLibrary) {
    return null;
  }
  const conflictingFolder = assetLibrary.assetFolders.find(folder => {
    return folder.id !== currentFolderId && normalizeFolderName(folder.name) === canonicalFolderName;
  });
  return conflictingFolder ? policy : null;
}
